import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StripeElementChangeEvent } from '@stripe/stripe-js';
import { IbanElement, useElements } from '@stripe/react-stripe-js';
import useStripeOptions from '@hooks/vendor/useStripeOptions';
import withStripe from '@hocs/withStripe';
import useSetupIban from './hooks/useSetupIban';
import FormContext from '../FormContext';
import { useTranslations } from 'next-intl';
import { PaymentMethodType } from '@monofront/types';
import ErrorFilledIcon from '@monofront/design-system/visuals/Icons/ErrorFilled';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import references from '@monofront/design-system/css-library/tokens/reference';
import {
  Input,
  ErrorText,
  TextInput,
  Label,
} from '@monofront/design-system/components/FormV2';

interface IIbanFormAbTestProps {
  onComplete?: (isValid: boolean) => void;
  handleSubmitIban: (
    paymentMethodId: string,
    paymentMethodeType: PaymentMethodType,
  ) => Promise<void> | void;
  handleFinish?: () => void;
  children: Array<ReactNode | Element>;
}

const IbanFormAbTest: React.FC<IIbanFormAbTestProps> = ({
  onComplete,
  handleSubmitIban,
  handleFinish,
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const elements = useElements();
  const options = useStripeOptions();
  const setupIban = useSetupIban();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    reset,
    watch,
    formState: { dirtyFields },
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });
  const ibanHolderName = watch('ibanHolderName', '');
  const [isIbanInputCompleted, setIsIbanInputCompleted] = useState(false);

  const handleSubmitForm = async (formData) => {
    setIsLoading(true);

    try {
      const ibanElement = elements?.getElement(IbanElement);
      const paymentIntent = await setupIban(
        formData.ibanHolderName,
        ibanElement!,
      );

      await handleSubmitIban(
        paymentIntent!.payment_method! as string,
        PaymentMethodType.IBAN,
      );

      ibanElement!.clear();
      handleFinish?.();
      reset();
    } catch (error: any) {
      setError('form', { type: error.errorKey, message: error.errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const tAccount = useTranslations('Account');

  useEffect(() => {
    if (
      isIbanInputCompleted &&
      'ibanHolderName' in dirtyFields &&
      !errors.ibanHolderName
    ) {
      onComplete?.(true);

      return;
    }

    onComplete?.(false);
  }, [dirtyFields, ibanHolderName, errors, isIbanInputCompleted]);

  return (
    <FormContext.Provider
      value={useMemo(
        () => ({ clearErrors, errors, isLoading, register, setError }),
        [clearErrors, errors, isLoading, register, setError],
      )}
    >
      <form onSubmit={handleSubmit(handleSubmitForm)}>
        <label htmlFor="bank-number" className="tw-mb-1 tw-block">
          {tAccount('ibanLabel')}
        </label>
        <IbanElement
          id="bank-number"
          onChange={(event: StripeElementChangeEvent) => {
            if (!event.error) {
              clearErrors('form');
            } else {
              setError('form', {
                type: event.error.type,
                message: event.error.message,
              });
            }

            setIsIbanInputCompleted(event.complete);
          }}
          options={{ ...options, supportedCountries: ['SEPA'] }}
        />
        {errors.form && (
          <p className="tw-mt-1 tw-flex tw-flex-row">
            <ErrorFilledIcon
              size={IconSizes.sm}
              color={references.colors.red[600]}
            />
            <p className="tw-lf-body-regular-sm tw-ml-2 tw-text-red-600">
              {errors.form.message as string}
            </p>
          </p>
        )}
        <TextInput
          id="ibanHolderName"
          isInvalid={!!errors.ibanHolderName?.message}
          UNSAFE_className="tw-mt-4"
        >
          <Label>{tAccount('ibanOwnerLabel')}</Label>
          <Input
            placeholder={tAccount('ibanOwnerInputPlaceholder')}
            {...register('ibanHolderName', {
              required: {
                value: true,
                message: tAccount('mandatoryField'),
              },
            })}
            data-testid="IbanFormAbTest-Input-name"
            name="ibanHolderName"
          />
          <ErrorText>{errors.ibanHolderName?.message as string}</ErrorText>
        </TextInput>
        {children as ReactNode}
      </form>
    </FormContext.Provider>
  );
};

export default withStripe<IIbanFormAbTestProps>(IbanFormAbTest);
